// src/utils/reservation-display.js
// 訂位顯示共用 helper。
//
// 這些原本是 RestaurantReservationsPage.js 的模組內私有函式，
// 119「新進訂單」需要在營運總覽用同一套顯示規則（同樣的桌號寫法、同樣的稱謂、
// 同樣的狀態顏色），所以抽出來共用。
//
// ⚠️ 抽出時是**逐字搬移，沒有任何行為變更** —— 訂單管理頁的畫面必須完全一樣。
// 兩邊要顯示得一致，改這裡就好；不要在任何一頁重新定義。

import React from 'react';
import { Box, Stack, Typography } from '@mui/material';

// ──────────────────────────────────────────────────────────────
// 日期 / 時間
// ──────────────────────────────────────────────────────────────

export const todayYmd = () => {
  const fmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric', month: '2-digit', day: '2-digit'
  });
  return fmt.format(new Date());
};

export const fmtTime = (t) => {
  if (!t) return '';
  return String(t).slice(0, 5);
};

// ──────────────────────────────────────────────────────────────
// 姓名 / 稱謂
// ──────────────────────────────────────────────────────────────

// Compose display "name 先生/小姐" from canonical (bare name + gender column).
// Migration 109: gender now lives in customer_gender column, name is bare.
// Falls back to parsing suffix from name when gender column is NULL (e.g.
// legacy row not yet backfilled), then defaults to 先生 if still unknown.
export const formatDisplayName = (name, gender) => {
  if (!name) return '';
  const s = String(name).trim();
  if (gender === 'ms') return `${s} 小姐`;
  if (gender === 'mr') return `${s} 先生`;
  if (s.endsWith('小姐') || s.endsWith('先生')) return s; // legacy: suffix on name
  return `${s} 先生`; // last-resort fallback
};

// ──────────────────────────────────────────────────────────────
// 狀態 / 來源
// ──────────────────────────────────────────────────────────────

export const STATUS_LABEL = {
  confirmed: '已確認',
  seated: '用餐中',
  cancelled: '已取消',
  no_show: '未到',
};
export const STATUS_COLOR = {
  confirmed: '#145da0',
  seated: '#4caf50',
  cancelled: '#9e9e9e',
  no_show: '#ff9800',
};

// 訂位來源（reservations.source）：voice=電話AI 語音訂位 / manual=後台手動 / web=線上訂位 /
// voice_callback=AI 收下需求（超過人數上限等）、老闆回電後在後台確認成交（T-056）。
// ⚠️ 要統計「AI 語音帶進來的生意」時，voice 與 voice_callback **兩個都要算**。
// 中性樣式（顏色固定，只有文字變）— 見 uniform chip styling 慣例；無 emoji。
export const SOURCE_LABEL = {
  voice: '電話AI',
  voice_callback: '電話AI回撥',
  manual: '手動',
  web: '網路',
};

export const StatusDot = ({ status, size = 13 }) => (
  <Stack direction="row" spacing={1} alignItems="center">
    <Box sx={{ width: Math.round(size * 0.62), height: Math.round(size * 0.62), borderRadius: '50%', bgcolor: STATUS_COLOR[status] || '#9e9e9e', flexShrink: 0 }} />
    <Typography sx={{ fontSize: size }}>{STATUS_LABEL[status] || status}</Typography>
  </Stack>
);

// 來源小標籤（中性色，無 emoji）。未知/空來源不顯示，避免雜訊。
export const SourceChip = ({ source }) => {
  const label = SOURCE_LABEL[source];
  if (!label) return null;
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-block', ml: 0.75, px: 0.75,
        fontSize: 11, lineHeight: '18px', borderRadius: '4px',
        bgcolor: '#eceff1', color: '#546e7a', fontWeight: 500,
        verticalAlign: 'middle', whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
};

// ──────────────────────────────────────────────────────────────
// 加購 / 備註標籤
// ──────────────────────────────────────────────────────────────

export const EXTRA_CHIP_PALETTE = {
  duck:       { bg: '#fff3e0', fg: '#e65100' },
  kidChairs:  { bg: '#e8f5e9', fg: '#2e7d32' },
  babySeat:   { bg: '#e3f2fd', fg: '#145da0' },
};
export const extraChipColor = (key) => EXTRA_CHIP_PALETTE[key] || { bg: '#fff3e0', fg: '#e65100' };

// underscore-prefixed keys are system-managed (e.g. _adults / _children).
// Filter them out of badge rendering.
export const isUserExtraKey = (k) => !k.startsWith('_');

// 非 addonConfig 的系統 extra key → 友善顯示名（如線上訂位帶來的 specialRequests）。
export const EXTRA_KEY_LABELS = { specialRequests: '特殊需求' };

export const renderExtraBadges = (extras, addonConfig, fontSize = 11) => {
  if (!extras || typeof extras !== 'object') return null;
  const keys = Object.keys(extras).filter(isUserExtraKey);
  if (keys.length === 0) return null;
  const labelByKey = new Map((addonConfig || []).map(a => [a.key, a.label || a.key]));
  const items = [];
  for (const key of keys) {
    const v = extras[key];
    const isEmpty = v === false || v === null || v === undefined || v === 0
      || (Array.isArray(v) && v.length === 0) || v === '';
    if (isEmpty) continue;
    const label = labelByKey.get(key) || EXTRA_KEY_LABELS[key] || key;
    const palette = extraChipColor(key);
    let display;
    if (typeof v === 'boolean') display = label;
    else if (typeof v === 'number') display = `${label} ${v}`;
    else if (Array.isArray(v)) display = `${label}：${v.join('、')}`;
    else display = `${label}：${v}`;
    items.push(
      <Box key={key} sx={{
        display: 'inline-flex', alignItems: 'center', px: 0.75, py: 0.1,
        fontSize, borderRadius: '8px', bgcolor: palette.bg, color: palette.fg,
      }}>
        {display}
      </Box>
    );
  }
  if (items.length === 0) return null;
  return <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>{items}</Stack>;
};

// ──────────────────────────────────────────────────────────────
// 桌號
// ──────────────────────────────────────────────────────────────

// 桌號顯示：純數字桌號補「桌」（紅舍 6→6桌，避免只看到數字被誤會）；非數字（如 A3）維持原樣；無桌位＝候補。
export const oneTableLabel = (n) => {
  const s = String(n);
  return /^\d+$/.test(s) ? `${s}桌` : s;
};
// 列表 chip（壓縮，避免手機一直往橫長）：單桌 3桌；2–3 桌 3+5桌；≥4 桌 併N桌；無 候補
export const tableChipLabel = (r) => {
  const combined = Array.isArray(r.combinedTables) ? r.combinedTables : [];
  if (combined.length >= 2) {
    if (combined.length >= 4) return `併${combined.length}桌`;
    const nums = combined.map((t) => String(t.tableNumber));
    return nums.every((s) => /^\d+$/.test(s)) ? `${nums.join('+')}桌` : nums.join('、');
  }
  const n = r.table?.tableNumber;
  if (!n && n !== 0) return '候補';
  return oneTableLabel(n);
};
// 完整桌號（tooltip / title 用）：併桌：3、5、7、8、9桌
export const tableFullLabel = (r) => {
  const combined = Array.isArray(r.combinedTables) ? r.combinedTables : [];
  if (combined.length >= 2) {
    const nums = combined.map((t) => String(t.tableNumber));
    const body = nums.every((s) => /^\d+$/.test(s)) ? `${nums.join('、')}桌` : nums.join('、');
    return `併桌：${body}`;
  }
  const n = r.table?.tableNumber;
  return (!n && n !== 0) ? '候補' : oneTableLabel(n);
};

// ──────────────────────────────────────────────────────────────
// 119 新進訂單專用
// ──────────────────────────────────────────────────────────────

const WEEKDAY_TW = ['日', '一', '二', '三', '四', '五', '六'];

// 'YYYY-MM-DD' → '8/22（六）'。用 (y, m-1, d) 本地建構，星期不受時區影響。
export const fmtDateWithWeekday = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return String(ymd || '');
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return `${Number(m[2])}/${Number(m[3])}（${WEEKDAY_TW[d.getDay()]}）`;
};

// 「這筆是多久以前進來的」。近 1 小時的會被標成新進（見 RECENT_HIGHLIGHT_MINUTES）。
export const minutesSince = (iso) => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return null;
  return Math.floor((Date.now() - t) / 60000);
};

export const fmtRelativeTime = (iso) => {
  const m = minutesSince(iso);
  if (m === null) return '';
  if (m < 0) return '剛剛';        // 伺服器時鐘略快時不要顯示負數
  if (m < 1) return '剛剛';
  if (m < 60) return `${m} 分鐘前`;
  if (m < 1440) return `${Math.floor(m / 60)} 小時前`;
  return `${Math.floor(m / 1440)} 天前`;
};

// ──────────────────────────────────────────────────────────────
// 字級（由 navbar 全域「A」的 fontScale 驅動，訂單管理與新進訂單共用）
// ──────────────────────────────────────────────────────────────

// 手機/平板卡片基準字級。沿用訂單管理已在 stage 驗過的 20/22/24 視覺。
export const MOBILE_CARD_FS = { 1: 20, 1.18: 22, 1.38: 24 };
export const scaleToCardFs = (scale) => MOBILE_CARD_FS[scale] || 20;

// 桌機表格 cell 字級。訂單管理是 16×scale；營運總覽「新進訂單」在
// Jasper 實機看過後加大 4px（13.5 → 17.5），同樣吃全域 A 一起縮放。
export const scaleToCellFs = (scale) => Math.round(16 * scale);
export const scaleToRecentCellFs = (scale) => Math.round(17.5 * scale);

// 近 1 小時進來的 → 整列淡藍底
export const RECENT_HIGHLIGHT_MINUTES = 60;
export const NEW_ROW_BG = '#f6fbff';

// 從推播／新進訂單跳過來時，被標出的那一列
export const HL_BG = '#fff8e1';
export const HL_BAR = '#ffb300';
